import React, { useContext, useEffect, useRef, useState } from 'react';
import './Upload.scss';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import Modal from '../Modal/Modal';
import { ReactSVG } from 'react-svg';
import check from '../../Assets/icons/check.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApiContext from '../../Api/ApiContext';
import { Paths } from '../../Core/EPath';
import useAnalytics from '../../Analytics/Analytics';
import { selectResellerConfig, updateLegalFlyId } from '../../Store/configSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../Store/store';

const Upload = (props: any) => {
  const { update, nds, anonymise, groupId, hovering, type, id, max } = props;
  const intervalRef = useRef(null);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState(null);
  const [uploadTime, setUploadTime] = useState(0);

  const api = useContext(ApiContext);
  const resellerConfig = useSelector(selectResellerConfig)
  const params = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const { trackEvent, EventTypes } = useAnalytics();

  useEffect(() => {
    let dragCounter = 0;

    const change = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter++;
      if (dragCounter === 1) {
        const zones = document.querySelectorAll('.zone');

        zones.forEach(zone => {
            zone.classList.add('dragging');
        });
      }
    };

    const change_back = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter--;
      if (dragCounter === 0) {
        const zones = document.querySelectorAll('.zone');

        zones.forEach(zone => {
            zone.classList.remove('dragging');
        });
      }
    };

    const reset_counter = (e) => {
      dragCounter = 0;
      const zones = document.querySelectorAll('.zone');

      zones.forEach(zone => {
          zone.classList.remove('dragging');
      });
    };

    document.body.addEventListener('dragenter', change, false);
    document.body.addEventListener('dragleave', change_back, false);
    document.body.addEventListener('drop', reset_counter, false);

    return () => {
      document.body.removeEventListener('dragenter', change, false);
      document.body.removeEventListener('dragleave', change_back, false);
      document.body.removeEventListener('drop', reset_counter, false);
    };
  }, []);

  useEffect(() => {
    if (uploadComplete) {
      clearInterval(intervalRef.current);
      setTimeout(() => {
        setUploadComplete(false);
        setLoading(false);
        setUploadTime(0);
      }, 3000);
      return;
    }

    if (!loading) return;

    intervalRef.current = setInterval(() => {
      if (uploadTime < 2) {
        setUploadTime(uploadTime + 1);
      } else {
        clearInterval(intervalRef.current);
      }
    }, 1000);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [uploadTime, loading, uploadComplete]);

  async function onDrop(files) {

    if(type === "notes") {
      update([files[0]]);
      setUploadComplete(true);
      return;
    }

    setLoading(true);

    trackEvent(EventTypes.UPLOAD, {
      uploadType: type,
      fileAmount: files.length,
      fileSizes: files.reduce((acc, file) => acc + file.size, 0),
      fileTypes: Array.from(new Set(files.map(file => file.type))).join(', ')
    });

    const GeneralErrorModal = () => {
      return (
        <div>
          <div className="circle">
            <div className="innerCircle"></div>
          </div>
          <p>{t('upload.general_error_modal.description')}</p>
        </div>
      );
    };

    const CreditsFinishedModal = () => {
      return (
        <div>
          <div className="circle">
            <div className="innerCircle"></div>
          </div>
          <p>{t('upload.credits_finished_modal.description')}</p>
        </div>
      );
    };

    const FileSizeExceededModal = () => {
      return (
        <div>
          <div className="circle">
            <div className="innerCircle"></div>
          </div>
          <p>{t('upload.file_size_exceeded_modal.description')}</p>
        </div>
      );
    };

    const FileTypeErrorModal = () => {
      return (
        <div>
          <div className="circle">
            <div className="innerCircle"></div>
          </div>
          <p>{t('upload.file_type_error_modal.description')}</p>
        </div>
      );
    };

    const UploadErrorModal = () => {
      return (
        <div>
          <div className="circle">
            <div className="innerCircle"></div>
          </div>
          <p>{t('upload.upload_error_modal.description')}</p>
        </div>
      );
    };

    let processedFiles = [];
    let conversationId = id;

    for (let index = 0; index < files.length; index++) {
      await processFile(index);
    };

    async function processFile(index) {
      const file = files[index];

      const data = new FormData();
      data.append('file' + index, file);
      if(anonymise)
        data.append('anonymise', anonymise);
      if(groupId)
        data.append('group', groupId);
      if(id)
        data.append('id', id);

      let newFlow = false;
      let endpoint = '/upload';

      let upload_url_endpoint = '/upload_url';
      let verify_endpoint = '/verify';

      switch(type) {
        case "discovery":
          upload_url_endpoint = '/fly/upload';
          verify_endpoint = '/fly/upload/verify';
          newFlow = true;
          break;
        case "draft-template":
          upload_url_endpoint = "/drafts/templates";
          verify_endpoint = "/drafts/templates/verify";
          newFlow = true;
          break;
        case "training":
          upload_url_endpoint = '/training/documents/upload_url';
          verify_endpoint = '/training/documents/verify';
          newFlow = true;
          break;
        default:
          break;
      }

      if(newFlow) {

        if(
          file.type !== "application/pdf" &&
          file.type !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document" &&
          file.type !== "application/msword" &&
          file.type !== "image/jpeg" && 
          file.type !== "image/png"
        ) {
          setLoading(false);
          setShowModal(true);
          setModalText(<FileTypeErrorModal />);
          return;
        }

        const uploadResponse = await api.post(upload_url_endpoint, {
          filename: file.name,
          type: file.type,
          id: conversationId
        });
    
        const uploadUrl = uploadResponse.data.uploadUrl;
        let documentId = uploadResponse.data?.documentId;
        conversationId = uploadResponse.data?.conversationId;
    
        if (type === "discovery") {
          documentId = uploadResponse.data.source.id;
          processedFiles.push(uploadResponse.data.source); // Assuming 'processedFiles' is defined elsewhere
        }
    
        // Second API call to upload the file
        let uploadResult;
        try {
          uploadResult = await axios.put(uploadUrl, file, {
            headers: {
              'Content-Type': file.type
            }
          });
        } catch (error) {
          setLoading(false);
          setShowModal(true);
          setModalText(<UploadErrorModal />);

          if(type === "training") {
            try {
              await api.delete(`/training/documents/${documentId}`);
            } catch (error) {
                console.error('Error deleting document:', documentId, error);
            }
          }
          return;
        }
    
        if (uploadResult.status === 200) {
          // Third API call to verify the upload
          await api.post(verify_endpoint, {
            documentId,
            id: conversationId
          });
    
          if (index === files.length - 1) {
            if (type !== "discovery") {
              update(documentId); // Assuming 'update' function is defined elsewhere
            } else {
              update({ sources: processedFiles, conversationId }); // Same assumption as above
            }
            setUploadComplete(true); // Assuming 'setUploadComplete' is defined elsewhere
          }
        } else {
          setLoading(false);
          setShowModal(true);
          setModalText(<UploadErrorModal />);

          if(type === "training") {
            try {
              await api.delete(`/training/documents/${documentId}`);
            } catch (error) {
                console.error('Error deleting document:', documentId, error);
            }
          }
        }

      } else {
        if(resellerConfig) {
          data.append('resellerDocumentId', params.id);
        }
        api.post(endpoint, data).then(async (res) => {
          if (res.data.message && res.data.message === 'generalError') {
            setLoading(false);
            setShowModal(true);
            setModalText(<GeneralErrorModal />);
            return;
          }

          if (res.data.message && res.data.message === 'size') {
            setLoading(false);
            setShowModal(true);
            setModalText(<FileSizeExceededModal />);
            return;
          }

          if (res.data.message && res.data.message === 'empty') {
            setLoading(false);
            setShowModal(true);
            setModalText(<FileTypeErrorModal />);
            return;
          }

          if (res.data.message && res.data.message === 'creditsShortage') {
            setLoading(false);
            setShowModal(true);
            setModalText(<CreditsFinishedModal />);
            if(type === "discovery" && res.data.sources.length > 0) {
              update(res.data);
            }
            return;
          }

          if (res.data.message && res.data.message === 'unsupportedFile') {
            setLoading(false);
            setShowModal(true);
            setModalText(<FileTypeErrorModal />);
            return;
          }

          if (res.data.message && res.data.message === 'creditsDone') {
            setLoading(false);
            setShowModal(true);
            setModalText(<CreditsFinishedModal />);
            return;
          }

          switch(type) {
            case "review":
              navigate(Paths.REVIEW_DETAIL({ id: res.data.id }))
              break;
            default:
              update(res.data);
              break;
          }

          if (index === files.length - 1) {
            setUploadComplete(true);
          }

          if(resellerConfig) {
            dispatch(updateLegalFlyId(res.data.id));
            navigate(Paths.REVIEW_DETAIL({ id: params.id }));
          }
        });
      }
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      onDrop(files);
    },
  });

  if (uploadComplete) {
    return (
      <div className="upload">
        <div className="dropToUpload complete">
          <div className="checkmark">
            <ReactSVG src={check} />
          </div>
          <h3>{t('upload.upload_complete')}</h3>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="upload">
        <div className="dropToUpload loading">
          <h3>{t('upload.uploading')}</h3>
          <div className="progress-bar">
            <div className="progress" style={{ width: `${(uploadTime / 2) * 100}%` }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal showModal={showModal} setShowModal={setShowModal} title={"Error"} content={<>{modalText}</>} />
      <div className={`upload ${nds ? 'upload-nds' : ''}`} {...getRootProps()} data-click-target="upload" data-click-source={type}>
        <input {...getInputProps()} />
        <div className={`dropToUpload ${isDragActive ? 'hovering' : ''}`}>
          <div className="plus">+</div>
          <h3>{isDragActive ? t('upload.drop') : (hovering? t('dash.drop') : t('upload.upload_or_drag'))}</h3>
        </div>
        <div id="dropzone" className="dropToUpload zone"></div>
      </div>
    </>
  );
};

export default Upload;
